import { IconAlertTriangle } from "@tabler/icons-react";
import { observer } from "mobx-react";
import { useEffect, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Scope, ScopeCategory } from "@/services/oauth-idp.service";
import { useTranslation } from "@/i18n";

/**
 * Shared scope-selection primitive.
 *
 * Backs every scope-choice surface in the product — OAuth app admin,
 * workspace MCP configuration, end-user consent — so scope UX stays
 * uniform. New surfaces should reuse this rather than hand-rolling a
 * checkbox list.
 *
 * Two modes:
 * - ``single`` (default) — one column of checkboxes controlling
 *   ``value``. Used by MCP scope ceiling, consent, and the OAuth create
 *   flow (where the operator doesn't yet care about the default
 *   subset).
 * - ``matrix`` — two columns (Allowed / Default) controlling
 *   ``value`` and ``defaults``. The primitive enforces ``defaults ⊆
 *   value`` internally: unticking Allowed removes the same scope from
 *   Default, ticking Default forces Allowed. Used by the OAuth app
 *   edit page.
 *
 * Destructive scopes (name ending in ``.write``) always carry a
 * per-row warning icon + inline label so the user can see at a glance
 * which selections modify data. ``contextKind="mcp"`` additionally
 * renders an "AI can make mistakes" banner above the picker when any
 * destructive scope is selected — used by the workspace MCP
 * scope-ceiling dialog.
 */

/**
 * One heading per category the scope catalog can serve, in the order the
 * backend's catalog groups them (``SCOPE_CATALOG`` in ``scopes.py``) — the
 * object's key order IS the display order, see ``CATEGORY_ORDER``.
 *
 * ``Record`` over the whole union deliberately: ``ScopeCategory`` is derived
 * from the generated API types, so a category added on the backend fails the
 * type-check here until it is given a heading. Before that it rendered as its
 * raw id, which is how the qualifications group read "SKILLS" to admins
 * (NG-1668).
 */
/** Each category's heading key, in display order. */
const CATEGORY_LABELS: Record<ScopeCategory, string> = {
  identity: "scope_picker.category.identity",
  workspace: "scope_picker.category.workspace",
  work_items: "scope_picker.category.work_items",
  projects: "scope_picker.category.projects",
  requests: "scope_picker.category.requests",
  programs: "scope_picker.category.programs",
  goals: "scope_picker.category.goals",
  events: "scope_picker.category.events",
  calendar: "scope_picker.category.calendar",
  users: "scope_picker.category.users",
  teams: "scope_picker.category.teams",
  qualifications: "scope_picker.category.qualifications",
  workspace_configuration: "scope_picker.category.workspace_configuration",
};

// Derived, not a second list to keep in step: a category present in the labels
// but missing here would sort silently to the bottom, which is the quiet version
// of the bug above. Unknown categories still sort last — see ``categoryRank``.
const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS) as ScopeCategory[];

type ScopePickerMode = "single" | "matrix";
type ScopeContextKind = "default" | "mcp";

// A scope is "destructive" when granting it lets the bearer modify or
// delete workspace data. The OAuth scope catalog
// (``apps/backend/cognota/oauth_idp/scopes.py``) names such scopes with
// a ``.write`` or ``.delete`` suffix — a strict convention every entry
// follows (read = ``.read``, mutation = ``.write``, irreversible
// destruction = ``.delete``, no other suffixes). If a scope is ever
// added that doesn't follow this pattern, replace the heuristic with a
// server-provided ``is_destructive`` field on the ``Scope`` payload.
const DESTRUCTIVE_SUFFIXES = [".write", ".delete"];

function isDestructiveScope(scopeName: string): boolean {
  return DESTRUCTIVE_SUFFIXES.some((suffix) => scopeName.endsWith(suffix));
}

/**
 * Whether any of the given scope names would grant destructive access.
 * Exported so callers can gate a submit-button confirmation without
 * repeating the ``.write``-suffix heuristic — keeps every scope-choice
 * surface in step with the same rule.
 */
export function hasDestructiveScope(scopeNames: string[]): boolean {
  return scopeNames.some(isDestructiveScope);
}

export interface ScopePickerProps {
  /** Full catalog to render. Callers narrow it (e.g. to a platform
   *  ceiling) before passing. */
  catalog: Scope[];
  /** Currently-selected scope names. */
  value: string[];
  /** Called with the next selection when the user toggles a scope. */
  onChange: (next: string[]) => void;

  /** ``matrix`` shows a second Default column; ``single`` (default) omits it. */
  mode?: ScopePickerMode;

  /** Matrix mode: the current Default subset of ``value``. */
  defaults?: string[];
  /** Matrix mode: called when the Default subset changes. */
  onDefaultsChange?: (next: string[]) => void;

  /** Scope names that must be selected and cannot be unticked
   *  (rendered checked + disabled). Mandatory scopes on the consent
   *  screen, or ``openid`` in the OAuth create flow. */
  locked?: string[];

  /** Scope names to skip rendering entirely (e.g. mandatory-and-invisible
   *  identity scopes when the caller wants no UI at all for them). */
  hidden?: string[];

  /** Whole component is temporarily unresponsive (e.g. submitting). */
  disabled?: boolean;

  /** Display-only mode; renders no interactive checkboxes. */
  readOnly?: boolean;

  /** Loading state — renders a placeholder line. */
  loading?: boolean;

  /** Inline error shown above the list. */
  error?: string | null;

  /** Suppress the scope description. Off by default. */
  hideDescription?: boolean;

  /** Suppress the monospace scope name (e.g. ``workspaces.read``) and
   *  render only the human description. Used by the end-user consent
   *  screen where the identifier is developer-facing noise. */
  hideScopeName?: boolean;

  /** Wrap rows in category ``<fieldset>``s with the category name as
   *  a legend. On by default. Off for the consent screen, which
   *  consumes ``ConsentContext`` (no category field) and renders a
   *  single flat list. */
  groupByCategory?: boolean;

  /** What the picker is being used for. ``"mcp"`` renders an
   *  "AI can make mistakes" banner above the list when any destructive
   *  scope is selected; ``"default"`` (the OAuth-app admin picker,
   *  end-user consent, etc.) omits it. The per-row destructive-marker
   *  (warning icon + inline label) is independent of this and always
   *  shows for ``.write`` scopes. */
  contextKind?: ScopeContextKind;
}

interface Row {
  scope: Scope;
  isLocked: boolean;
  isSelected: boolean;
  isDefault: boolean;
  isDestructive: boolean;
}

/**
 * Compare category keys with unknown categories sorted after known
 * ones (fail-open — a new backend category still renders, just below
 * the recognised list).
 */
function categoryRank(category: ScopeCategory): number {
  const idx = CATEGORY_ORDER.indexOf(category);
  return idx === -1 ? CATEGORY_ORDER.length : idx;
}

/**
 * Heading for a category. A category the backend serves but this build has no
 * label for is grouped under "Other" rather than printed as its raw id: the
 * picker fails open (blanking a consent screen over a new category is worse),
 * but an id rendered as a heading reads as a real heading and reached admins
 * unnoticed for six categories (NG-1668). The component reports it — see the
 * effect in ``ScopePicker`` — so this stays a pure lookup.
 */
function categoryLabelKey(category: ScopeCategory): string {
  return CATEGORY_LABELS[category] ?? "scope_picker.category.other";
}

export const ScopePicker = observer(function ScopePicker({
  catalog,
  value,
  onChange,
  mode = "single",
  defaults,
  onDefaultsChange,
  locked,
  hidden,
  disabled = false,
  readOnly = false,
  loading = false,
  error = null,
  hideDescription = false,
  hideScopeName = false,
  groupByCategory = true,
  contextKind = "default",
}: ScopePickerProps) {
  const { t } = useTranslation();
  const lockedSet = useMemo(() => new Set(locked ?? []), [locked]);
  const hiddenSet = useMemo(() => new Set(hidden ?? []), [hidden]);
  const valueSet = useMemo(() => new Set(value), [value]);
  const defaultSet = useMemo(() => new Set(defaults ?? []), [defaults]);

  const visible = useMemo(
    () => catalog.filter((s) => !hiddenSet.has(s.name)),
    [catalog, hiddenSet],
  );

  // A category with no heading renders under "Other", which is deliberate but
  // silent — nobody would notice a group losing its name. Report it once per
  // catalog rather than once per render.
  const unlabelledCategories = useMemo(
    () =>
      Array.from(
        new Set(visible.map((s) => s.category).filter((c) => !(c in CATEGORY_LABELS))),
      ).sort(),
    [visible],
  );

  useEffect(() => {
    for (const category of unlabelledCategories) {
      console.warn("ScopePicker: no heading for scope category; grouped under Other", {
        category,
      });
    }
  }, [unlabelledCategories]);

  const grouped = useMemo(() => {
    const buckets = new Map<ScopeCategory, Scope[]>();
    for (const scope of visible) {
      const list = buckets.get(scope.category) ?? [];
      list.push(scope);
      buckets.set(scope.category, list);
    }
    const orderedKeys = Array.from(buckets.keys()).sort(
      (a, b) => categoryRank(a) - categoryRank(b),
    );
    return orderedKeys.map((category) => ({
      category,
      rows: buckets.get(category)!.map<Row>((scope) => ({
        scope,
        isLocked: lockedSet.has(scope.name),
        isSelected: valueSet.has(scope.name) || lockedSet.has(scope.name),
        isDefault: defaultSet.has(scope.name),
        isDestructive: isDestructiveScope(scope.name),
      })),
    }));
  }, [visible, valueSet, defaultSet, lockedSet]);

  // Any destructive scope that would actually be granted — anything
  // the user has selected OR that a caller has ``locked`` on. The MCP
  // banner reflects real risk, not just what's on-screen.
  const hasSelectedDestructive = useMemo(
    () =>
      visible.some(
        (s) => isDestructiveScope(s.name) && (valueSet.has(s.name) || lockedSet.has(s.name)),
      ),
    [visible, valueSet, lockedSet],
  );

  function toggleValue(scopeName: string, checked: boolean) {
    if (lockedSet.has(scopeName)) return;
    const next = new Set(valueSet);
    const nextDefaults = new Set(defaultSet);
    if (checked) {
      next.add(scopeName);
    } else {
      next.delete(scopeName);
      nextDefaults.delete(scopeName); // defaults ⊆ value invariant
    }
    onChange([...next]);
    if (mode === "matrix" && onDefaultsChange && nextDefaults.size !== defaultSet.size) {
      onDefaultsChange([...nextDefaults]);
    }
  }

  function toggleDefault(scopeName: string, checked: boolean) {
    if (mode !== "matrix" || !onDefaultsChange || lockedSet.has(scopeName)) return;
    const next = new Set(defaultSet);
    const nextValue = new Set(valueSet);
    if (checked) {
      next.add(scopeName);
      nextValue.add(scopeName); // ticking Default forces Allowed
    } else {
      next.delete(scopeName);
    }
    onDefaultsChange([...next]);
    if (nextValue.size !== valueSet.size) onChange([...nextValue]);
  }

  const matrix = mode === "matrix" && onDefaultsChange !== undefined;
  const interactiveDisabled = disabled || readOnly;

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground" role="status">
        {t("scope_picker.loading")}
      </div>
    );
  }

  if (visible.length === 0 && error === null) {
    return <div className="text-xs text-muted-foreground">{t("scope_picker.empty")}</div>;
  }

  return (
    <div className="space-y-4">
      {error ? (
        <div className="text-sm text-destructive" role="alert">
          {error}
        </div>
      ) : null}

      {contextKind === "mcp" && hasSelectedDestructive ? (
        <div
          role="note"
          data-testid="scope-picker-mcp-warning"
          className="flex items-start gap-2 rounded-sm border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <IconAlertTriangle stroke={2} className="mt-0.5 size-4 shrink-0" />
          <span>
            <strong className="font-semibold">{t("scope_picker.mcp_warning_title")}</strong>{" "}
            {t("scope_picker.mcp_warning_body")}
          </span>
        </div>
      ) : null}

      {matrix ? (
        <div className="grid grid-cols-[1fr_80px_80px] gap-2 px-4 text-xs font-medium text-muted-foreground">
          <span>{t("scope_picker.col_scope")}</span>
          <span className="text-center">{t("scope_picker.col_allowed")}</span>
          <span className="text-center">{t("scope_picker.col_default")}</span>
        </div>
      ) : null}

      {groupByCategory ? (
        grouped.map(({ category, rows }) => (
          <fieldset key={category}>
            <legend className="mb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {t(categoryLabelKey(category))}
            </legend>
            <div className="divide-y divide-border rounded-sm border border-border">
              {rows.map((row) => (
                <ScopeRow
                  key={row.scope.name}
                  row={row}
                  matrix={matrix}
                  hideDescription={hideDescription}
                  hideScopeName={hideScopeName}
                  disabled={interactiveDisabled}
                  onToggleValue={(checked) => toggleValue(row.scope.name, checked)}
                  onToggleDefault={(checked) => toggleDefault(row.scope.name, checked)}
                />
              ))}
            </div>
          </fieldset>
        ))
      ) : (
        <div className="divide-y divide-border rounded-sm border border-border">
          {grouped
            .flatMap(({ rows }) => rows)
            .map((row) => (
              <ScopeRow
                key={row.scope.name}
                row={row}
                matrix={matrix}
                hideDescription={hideDescription}
                hideScopeName={hideScopeName}
                disabled={interactiveDisabled}
                onToggleValue={(checked) => toggleValue(row.scope.name, checked)}
                onToggleDefault={(checked) => toggleDefault(row.scope.name, checked)}
              />
            ))}
        </div>
      )}
    </div>
  );
});

const ScopeRow = observer(function ScopeRow({
  row,
  matrix,
  hideDescription,
  hideScopeName,
  disabled,
  onToggleValue,
  onToggleDefault,
}: {
  row: Row;
  matrix: boolean;
  hideDescription: boolean;
  hideScopeName: boolean;
  disabled: boolean;
  onToggleValue: (checked: boolean) => void;
  onToggleDefault: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const gridCols = matrix ? "grid-cols-[1fr_80px_80px]" : "grid-cols-[1fr_auto]";
  return (
    <label
      className={`grid ${gridCols} items-start gap-2 px-4 py-2 text-sm`}
      data-testid={`scope-row-${row.scope.name}`}
    >
      <div className="min-w-0">
        <div className="break-words">
          {hideScopeName ? null : <span className="font-mono text-xs">{row.scope.name}</span>}
          {hideDescription ? null : (
            <span className={hideScopeName ? "text-sm" : "ml-2 text-xs text-muted-foreground"}>
              {row.scope.description}
            </span>
          )}
          {row.isLocked ? (
            <span className="ml-2 text-xs text-muted-foreground italic">
              {t("scope_picker.always_granted")}
            </span>
          ) : null}
        </div>
        {row.isDestructive ? (
          // Dedicated line under the description so the icon + label
          // can't wrap mid-sentence when the description line-breaks.
          <div className="mt-1 flex items-center gap-1 text-destructive">
            <Tooltip>
              <TooltipTrigger
                render={
                  <span
                    aria-label={t("scope_picker.destructive_aria")}
                    data-testid={`scope-destructive-icon-${row.scope.name}`}
                    className="inline-flex"
                  >
                    <IconAlertTriangle stroke={2} className="size-3.5" />
                  </span>
                }
              />
              <TooltipContent>{t("scope_picker.destructive_tooltip")}</TooltipContent>
            </Tooltip>
            <span className="text-xs font-medium">{t("scope_picker.modifies_data")}</span>
          </div>
        ) : null}
      </div>
      <span className="flex justify-center pt-0.5">
        <Checkbox
          checked={row.isSelected}
          disabled={disabled || row.isLocked}
          onCheckedChange={(state) => onToggleValue(state === true)}
          aria-label={row.scope.name}
        />
      </span>
      {matrix ? (
        <span className="flex justify-center pt-0.5">
          <Checkbox
            checked={row.isDefault}
            disabled={disabled || row.isLocked}
            onCheckedChange={(state) => onToggleDefault(state === true)}
            aria-label={t("scope_picker.default_aria", { name: row.scope.name })}
          />
        </span>
      ) : null}
    </label>
  );
});
